import React, { useState, useEffect } from "react";
import AdminLayout from "../../components/AdminLayout";
import Modal from "../../components/Modal";
import api from "../../services/api";
import { toast } from "react-toastify";
import { FiPlus, FiEdit2, FiTrash2, FiVideo } from "react-icons/fi";

const VideoManagement = () => {
  const [videos, setVideos] = useState([]);
  const [lifeSavers, setLifeSavers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    video_url: "",
    description: "",
    visibility_type: "all",
    visible_to: [],
    is_active: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [videosRes, lifeSaversRes] = await Promise.all([
        api.get("/videos/admin"),
        api.get("/life-savers"),
      ]);
      setVideos(videosRes.data);
      setLifeSavers(lifeSaversRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (video = null) => {
    if (video) {
      try {
        const response = await api.get(`/videos/${video.id}`);
        const videoData = response.data;
        setEditingVideo(videoData);
        setFormData({
          title: videoData.title || "",
          video_url: videoData.video_url || "",
          description: videoData.description || "",
          visibility_type: videoData.visibility_type || "all",
          visible_to: videoData.visible_to?.map((v) => v.life_saver_id) || [],
          is_active: videoData.is_active,
        });
      } catch (error) {
        toast.error("Failed to fetch video details");
        return;
      }
    } else {
      setEditingVideo(null);
      setFormData({
        title: "",
        video_url: "",
        description: "",
        visibility_type: "all",
        visible_to: [],
        is_active: true,
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingVideo(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleVisibilityChange = (lifeSaverId) => {
    setFormData((prev) => {
      const visible_to = prev.visible_to.includes(lifeSaverId)
        ? prev.visible_to.filter((id) => id !== lifeSaverId)
        : [...prev.visible_to, lifeSaverId];
      return { ...prev, visible_to };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = { ...formData };

      if (editingVideo) {
        await api.put(`/videos/${editingVideo.id}`, data);
        toast.success("Video updated successfully");
      } else {
        await api.post("/videos", data);
        toast.success("Video created successfully");
      }
      handleCloseModal();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this video?")) {
      try {
        await api.delete(`/videos/${id}`);
        toast.success("Video deleted successfully");
        fetchData();
      } catch (error) {
        toast.error("Failed to delete video");
      }
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="spinner"></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="page-header">
        <h1 className="page-title">Video Management</h1>
        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
          <FiPlus />
          Add Video
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>URL</th>
                <th>Visibility</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {videos.map((video) => (
                <tr key={video.id}>
                  <td>{video.title}</td>
                  <td>
                    <a
                      href={video.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {video.video_url.substring(0, 40)}...
                    </a>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        video.visibility_type === "all"
                          ? "badge-info"
                          : "badge-warning"
                      }`}
                    >
                      {video.visibility_type === "all"
                        ? "All"
                        : `${video.visible_to_count} selected`}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        video.is_active ? "badge-success" : "badge-danger"
                      }`}
                    >
                      {video.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="action-btn edit"
                      onClick={() => handleOpenModal(video)}
                      title="Edit"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(video.id)}
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
              {videos.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center">
                    No videos found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingVideo ? "Edit Video" : "Add Video"}
      >
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-section">
            <div className="form-section-header">
              <FiVideo className="form-section-icon" />
              <span>Video Information</span>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  className="form-input"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter video title"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Video URL <span className="required">*</span>
                </label>
                <input
                  type="url"
                  name="video_url"
                  className="form-input"
                  value={formData.video_url}
                  onChange={handleInputChange}
                  placeholder="https://www.youtube.com/watch?v=..."
                  required
                />
                <span className="form-hint">
                  Enter a valid YouTube or video URL
                </span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  className="form-textarea"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Enter video description (optional)"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-checkbox-container pt-2">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="form-checkbox  w-auto"
                  />
                  <span className="form-checkbox-label ps-2">
                     <strong>Active</strong> - Video will be visible on dashboards
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-header">
              <span>Visibility Settings</span>
            </div>

            <div className="visibility-card">
              <div className="visibility-options-grid">
                <label
                  className={`visibility-option-card ${
                    formData.visibility_type === "all" ? "selected" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="visibility_type"
                    value="all"
                    checked={formData.visibility_type === "all"}
                    onChange={handleInputChange}
                  />
                  <div className="visibility-option-content">
                    <span className="visibility-option-title">
                      All Members
                    </span>
                    <span className="visibility-option-desc">
                      Visible to everyone
                    </span>
                  </div>
                </label>
                <label
                  className={`visibility-option-card ${
                    formData.visibility_type === "specific" ? "selected" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="visibility_type"
                    value="specific"
                    checked={formData.visibility_type === "specific"}
                    onChange={handleInputChange}
                  />
                  <div className="visibility-option-content">
                    <span className="visibility-option-title">
                      Specific Members
                    </span>
                    <span className="visibility-option-desc">
                      Choose who can view
                    </span>
                  </div>
                </label>
              </div>

              {formData.visibility_type === "specific" && (
                <div className="lifesaver-selection">
                  <div className="lifesaver-selection-header">
                    <span>
                      Select Member ({formData.visible_to.length} selected)
                    </span>
                  </div>
                  <div className="lifesaver-list-modern">
                    {lifeSavers.map((ls) => (
                      <label
                        key={ls.id}
                        className={`lifesaver-item-modern ${
                          formData.visible_to.includes(ls.id) ? "selected" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.visible_to.includes(ls.id)}
                          onChange={() => handleVisibilityChange(ls.id)}
                        />
                        <div className="lifesaver-item-info">
                          <span className="lifesaver-name">{ls.full_name}</span>
                          <span className="lifesaver-email">{ls.email}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer-modern">
            <button
              type="button"
              className="btn btn-secondary-modern"
              onClick={handleCloseModal}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary-modern">
              {editingVideo ? "Update Video" : "Create Video"}
            </button>
          </div>
        </form>
      </Modal>
    </AdminLayout>
  );
};

export default VideoManagement;
